<script>
  import Modal from "@components/shared/modal.svelte";
  import Button from "@components/button.svelte";
  import DateRange from "@components/shared/date-range.svelte";
  import { Turbo } from "@hotwired/turbo-rails";
  import { MessageCircle, Trash2, CheckCheck } from "lucide-svelte";
  import { post } from "@lib/requests";
  import { isAfter, isValid, eachDayOfInterval, endOfMonth, format } from "date-fns";
  import { fr } from "date-fns/locale";
  import { getClinics, currentClinic, formatDateISO, showAlert } from "@lib/utils";
  import useFormData from "@hooks/use-form-data.svelte.js";
  import fill from 'fill-range'

  let { 
    open = $bindable(false),
    clinicId,
    startDate,
    endDate,
    days = [],
    title,
    message,
    onCreate,
    showButton = true,
  } = $props();

  let clinics = getClinics()
  let clinic = $derived(clinics.find(clinic => clinic.id == form.data.clinic_id))
  let modalTitle = $derived(clinic ? `Messages de calendrier pour ${clinic.name}` : "Messages de calendrier")

  let loading = $state(false)
  let error = $state(null)
  let messages = $state([])

  let daysOfWeek = [
    { label: "lundi", value: 1 },
    { label: "mardi", value: 2 },
    { label: "mercredi", value: 3 },
    { label: "jeudi", value: 4 },
    { label: "vendredi", value: 5 },
    { label: "samedi", value: 6 },
    { label: "dimanche", value: 0 },
  ]

  const form = useFormData({
    // NOTE: parentheses matter here; without them `vetId || currentUser()?.vet ? ...` will ignore `vetId`
    clinic_id: clinicId ?? currentClinic()?.id ?? null,
    start_date: formatDateISO(startDate || new Date()),
    end_date: formatDateISO(endDate || endOfMonth(new Date(), 1)),
    days_of_week: days || [],
    title: title || "",
    message: message || ""
  })

  let requiredFilled = $derived.by(() => {
    if (!form.data.days_of_week.length) return false
    let requiredFields = ["clinic_id", "start_date", "end_date", "title", "priority"]
    return requiredFields.every(field => form.data[field])
  })

  let datesValid = $derived.by(() => {
    if (!form.data.start_date || !form.data.end_date) return false
    if (!isValid(new Date(form.data.start_date)) || !isValid(new Date(form.data.end_date))) return false
    return !isAfter(form.data.start_date, form.data.end_date)
  })

  let valid = $derived(requiredFilled && datesValid)
  let disabled = $derived(!valid || !!messages.length)

  let selectedDates = $derived.by(() => {
    if (!datesValid || !form.data.days_of_week.length) return []
    const allDates = eachDayOfInterval({ start: form.data.start_date, end: form.data.end_date })
    return allDates.filter(date => form.data.days_of_week.includes(date.getDay()))
  })

  function selectDay(value, e) {
    if (!e.shiftKey) return
    const newDays = fill(form.data.days_of_week[form.data.days_of_week.length - 1], value)
    const days = [...form.data.days_of_week, ...newDays]
    form.data.days_of_week = Array.from(new Set(days))
  }
  
  async function handleSubmit(e) {
    e.preventDefault()
    if (!window.confirm(`Êtes-vous sûr de vouloir créer ${selectedDates.length} messages de calendrier ?`)) return
    try {
      loading = true
      const response = await post("/api/v1/calendar_messages/batch_create", { responseKind: "json", body: form.data })
      const data = await response.json
      if (response.ok) {
        messages = data.messages
      } else {
        error = data.error
      }
    } catch (error) {
      error = error.message || "Erreur lors de la création des messages de calendrier"
    } finally {
      loading = false
    }
  }

  function handleClose() {
    open = false
    form.reset()
    messages = []
    error = null
    onCreate?.() || refreshCalendar()
  }

  function refreshCalendar() {
    const frame = document.getElementById("calendar")
    if (frame && typeof frame.reload === "function") return frame.reload()
    Turbo.visit(location.href, { action: "replace" })
  }

</script>

{#if showButton}
  
  <Button naked text="Messages de calendrier" onclick={() => open = true} class="flex-row menu-item cursor-pointer">
    <MessageCircle size={16} class="text-gray-400 mr-2" />
    Messages de calendrier
  </Button>
{/if}

<Modal bind:open title={modalTitle} width="50rem" onClose={handleClose}>

  {#if error}
    <div class="app-message error">
      <i class="fal fa-exclamation-triangle"></i>
      <p>{error}</p>
    </div>
  {/if}

  {#if messages.length > 0}
    <div class="app-message success">
      <i class="fal fa-check-circle"></i>
      <p>{messages.length} message{messages.length > 1 ? "s" : ""} de calendrier créé{messages.length > 1 ? "s" : ""} avec succès</p>
    </div>
  
  {:else}
  <form id="calendar-message-assignation-form" onsubmit={handleSubmit}>

    <div class="form-items">

      <!-- Title -->
      <div class="form-item col-span-12">
        <label for="title">Titre</label>
        <input type="text" name="title" id="title" class="input" bind:value={form.data.title} required />
      </div>

      <!-- Days of the week  -->
      <div class="form-item col-span-12">
        <label for="days_of_week">Jours de la semaine</label>
        <div class="flex justify-between items-center mt-4">
          <div class="flex flex-wrap gap-4">
            {#each daysOfWeek as day}
              <label for={day.label} class="flex items-center gap-2" onclick={(e) => selectDay(day.value, e)}>
                {day.label}
                <input type="checkbox" name="days_of_week[]" value={day.value} id={day.label} bind:group={form.data.days_of_week} />
              </label>
            {/each}
          </div>
          <div class="flex gap-1 items-center">
            <Button naked iconSize={12} iconClass="text-gray-500" Icon={Trash2} hint="Effacer" onclick={() => form.data.days_of_week = []} />
            <Button naked iconSize={12} iconClass="text-gray-500" Icon={CheckCheck} hint="Tout sélectionner" onclick={() => form.data.days_of_week = daysOfWeek.map(day => day.value)} />
          </div>
        </div>
      </div>

      <!-- Date range -->
      <DateRange 
        bind:start={form.data.start_date} 
        bind:end={form.data.end_date} 
        labels={["Effectif à partir du", "Effectif jusqu'au"]}
        options="future" 
        class="col-span-12" 
        disabled={loading}
      />

      <!-- Priority select -->
      <div class="form-item col-span-12">
        <label for="priority">Priorité</label>
        <select name="priority" id="priority" class="select" bind:value={form.data.priority} required>
          <option value="normal">Normal</option>
          <option value="high">Haute</option>
        </select>
      </div>

      {#if selectedDates.length > 0}
        
        <!-- Selected dates -->          
        <div class="form-item col-span-12">
          <label for="selected-dates">Dates sélectionnées</label>
          <div class="p-4 border rounded text-xs text-slate-600 flex items-center gap-1 border-blue-100 bg-blue-50/70">
            <details class="overflow-y-auto max-h-[200px] w-full" open>
              <summary class="text-slate-700 font-medium hover:underline cursor-pointer">
                {#if loading}
                  <span class="loading">Chargement...</span>
                {:else}
                  {selectedDates.length} dates sélectionnées
                {/if}
              </summary>
              <ul class="flex flex-col gap-1 flex-wrap text-xs text-slate-600 mt-2">
                {#each selectedDates as date}
                  <li class="italic text-slate-500">
                    {format(date, "EEEE d MMMM yyyy", { locale: fr })}
                  </li>
                {/each}
              </ul>
            </details>
          </div>
        </div>

      {/if}

      <!-- Message -->
      <div class="form-item col-span-12">
        <label for="message">Message</label>
        <textarea name="message" id="message" class="textarea" bind:value={form.data.message}></textarea>
      </div>
      
    </div>

    
  </form>
  {/if}

  {#snippet footerButtons()}
    <Button 
      type="submit"
      class="button outline-submit" 
      text="Créer messages"
      form="calendar-message-assignation-form" 
      {disabled} 
      {loading}
      onclick={handleSubmit}
      Icon={CheckCheck} />
  {/snippet}
  
</Modal>